import { ThemeEvents } from '@theme/events';

/**
 * Product metafield tabs: variant-aware gemstones & details, static product tabs otherwise.
 * Listens for variant updates within Product information / quick add / featured product.
 */
class ProductMetafieldTabs extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {{ fallback: { gemstones: string; details: string }; byVariant: Record<string, { gemstones: string; details: string }>; static: Record<string, string> } | null} */
  #config = null;

  connectedCallback() {
    this.#loadConfig();
    // Must use `document`: variant:update dispatches on `variant-picker` and bubbles. A listener on
    // this component's `closest('.shopify-section')` only fires if that section is an *ancestor* of
    // the variant-picker. On split layouts, picker and tabs can sit in different branches under `main`
    // so the event never hits a section-level listener. Document always receives the bubble.
    this.#abort = new AbortController();
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal: this.#abort.signal });

    this.querySelectorAll('[data-pmt-tab]').forEach((btn) => {
      btn.addEventListener('click', this.#onTabClick);
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #loadConfig() {
    const el = this.querySelector('script[type="application/json"][data-product-metafield-tabs-config]');
    if (!el?.textContent) return;
    try {
      this.#config = JSON.parse(el.textContent);
    } catch {
      this.#config = null;
    }
  }

  /** @param {string} html */
  #isMeaningful(html) {
    return Boolean(html && html.replace(/<!--[\s\S]*?-->/g, '').trim());
  }

  /**
   * After variant-picker fetch, `updateVariantPicker` morphs the live DOM, then `VariantUpdateEvent` is
   * fired — so the morphed `script[data-selected-variant]` matches the new selection. Prefer this over
   * `event.detail.resource`, which is parsed from a generic first-match script in the *response* HTML.
   * @param {string | undefined} productId
   * @returns {string | number | undefined}
   */
  #getSelectedVariantIdFromLiveDom(productId) {
    if (!productId) return undefined;
    const id = String(productId);
    const vps = document.querySelectorAll('variant-picker');
    for (const el of vps) {
      if (el instanceof HTMLElement && String(el.dataset.productId ?? '') === id) {
        const script = el.querySelector('script[data-selected-variant]');
        const text = script?.textContent;
        if (!text) continue;
        try {
          const v = JSON.parse(text);
          if (v && typeof v === 'object' && v.id != null) return v.id;
        } catch {
          // ignore
        }
      }
    }
    return undefined;
  }

  /**
   * Per field: if this variant’s HTML is non-empty, use it (overrides product). Otherwise use
   * product-level `fallback` from the config (same as Liquid: variant when set, else product).
   * @param {string | number | undefined} variantId
   * @param {'gemstones' | 'details'} field
   */
  #resolveVariantField(variantId, field) {
    const cfg = this.#config;
    if (!cfg) return '';
    const id = variantId != null ? String(variantId) : '';
    const byV = /** @type {Record<string, { gemstones?: string; details?: string }> | undefined} */ (cfg.byVariant);
    const row = id && byV ? byV[id] : undefined;
    const fromVariant = row?.[field] ?? '';
    if (this.#isMeaningful(fromVariant)) return fromVariant;
    return cfg.fallback[field] ?? '';
  }

  /** @param {MouseEvent & { target: EventTarget | null }} event */
  #onTabClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest('[data-pmt-tab]');
    if (!(btn instanceof HTMLButtonElement)) return;
    if (btn.classList.contains('pmt__tab--hidden') || btn.hidden) return;
    const key = btn.dataset.pmtTab;
    if (key) this.#activateTab(key);
  };

  /** @param {string} key */
  #activateTab(key) {
    this.querySelectorAll('[data-pmt-tab]').forEach((b) => {
      if (!(b instanceof HTMLButtonElement)) return;
      const sel = b.dataset.pmtTab === key;
      b.setAttribute('aria-selected', sel ? 'true' : 'false');
      b.classList.toggle('is-active', sel);
    });
    this.querySelectorAll('[data-pmt-panel]').forEach((p) => {
      if (!(p instanceof HTMLElement)) return;
      const show = p.dataset.pmtPanel === key;
      if (show) {
        p.removeAttribute('hidden');
      } else {
        p.setAttribute('hidden', '');
      }
      p.classList.toggle('is-active', show);
    });
  }

  #syncActiveTabAfterVariantChange() {
    const activeBtn = this.querySelector('[data-pmt-tab].is-active');
    let key =
      activeBtn instanceof HTMLButtonElement && activeBtn.dataset.pmtTab ? activeBtn.dataset.pmtTab : undefined;
    if (
      !key ||
      !(activeBtn instanceof HTMLButtonElement) ||
      activeBtn.hidden ||
      activeBtn.classList.contains('pmt__tab--hidden')
    ) {
      const first = this.querySelector('[data-pmt-tab]:not(.pmt__tab--hidden):not([hidden])');
      key = first instanceof HTMLButtonElement && first.dataset.pmtTab ? first.dataset.pmtTab : undefined;
    }
    if (key) this.#activateTab(key);
  }

  /**
   * `VariantUpdateEvent` / `VariantSelectedEvent` extend `Event` and assign `.detail` manually — they are
   * not `CustomEvent` instances, so a `instanceof CustomEvent` guard would always skip the handler.
   * @param {Event & { detail?: { data?: { html?: Document; productId?: string; newProduct?: { id: string } }; resource?: { id?: string | number } } }} event
   */
  #onVariantUpdate = (event) => {
    const detail = event && 'detail' in event ? event.detail : undefined;
    if (!detail?.data) return;

    // Match product-form: use event detail (source of truth) for product identity.
    if (detail.data.newProduct) {
      this.dataset.productId = String(detail.data.newProduct.id);
    } else if (String(detail.data.productId ?? '') !== String(this.dataset.productId ?? '')) {
      return;
    }

    this.#loadConfig();
    if (!this.#config) {
      return;
    }

    const productId = this.dataset.productId;

    // 1) Live morphed DOM (same source the customer sees) — most reliable.
    // 2) detail.resource from variant-picker (from fetched HTML, first script match).
    // 3) Matching variant-picker in the fetched `Document` (full page or section response).
    let resource = detail.resource;
    const fetched = detail.data.html;
    if (fetched && productId) {
      for (const vp of fetched.querySelectorAll('variant-picker')) {
        if (vp instanceof HTMLElement && String(vp.dataset.productId ?? '') === String(productId)) {
          const text = vp.querySelector('script[data-selected-variant]')?.textContent;
          if (text) {
            try {
              const parsed = JSON.parse(text);
              if (parsed && typeof parsed === 'object' && 'id' in parsed) {
                resource = /** @type {typeof resource} */ (parsed);
              }
            } catch {
              // keep previous resource
            }
          }
          break;
        }
      }
    }

    const variantId =
      this.#getSelectedVariantIdFromLiveDom(productId) ?? (resource && 'id' in resource ? resource.id : undefined);
    const gems = this.#resolveVariantField(variantId, 'gemstones');
    const details = this.#resolveVariantField(variantId, 'details');

    const gemsSlot = this.querySelector('[data-pmt-slot="gemstones"]');
    const detailsSlot = this.querySelector('[data-pmt-slot="details"]');
    if (gemsSlot) gemsSlot.innerHTML = gems;
    if (detailsSlot) detailsSlot.innerHTML = details;

    const gemsBtn = this.querySelector('[data-pmt-tab="gemstones"]');
    const gemsPanel = this.querySelector('[data-pmt-panel="gemstones"]');
    if (gemsBtn instanceof HTMLButtonElement && gemsPanel instanceof HTMLElement) {
      const show = this.#isMeaningful(gems);
      gemsBtn.classList.toggle('pmt__tab--hidden', !show);
      gemsBtn.hidden = !show;
      if (!show) {
        gemsPanel.setAttribute('hidden', '');
        gemsPanel.classList.add('pmt__panel--hidden');
      } else {
        gemsPanel.classList.remove('pmt__panel--hidden');
      }
    }

    const detailsBtn = this.querySelector('[data-pmt-tab="details"]');
    const detailsPanel = this.querySelector('[data-pmt-panel="details"]');
    if (detailsBtn instanceof HTMLButtonElement && detailsPanel instanceof HTMLElement) {
      const show = this.#isMeaningful(details);
      detailsBtn.classList.toggle('pmt__tab--hidden', !show);
      detailsBtn.hidden = !show;
      if (!show) {
        detailsPanel.setAttribute('hidden', '');
        detailsPanel.classList.add('pmt__panel--hidden');
      } else {
        detailsPanel.classList.remove('pmt__panel--hidden');
      }
    }

    this.#syncActiveTabAfterVariantChange();
  };
}

if (!customElements.get('product-metafield-tabs')) {
  customElements.define('product-metafield-tabs', ProductMetafieldTabs);
}
